/**
 * @module AI chat message types
 * We have to juggle three kinds of messages:
 * - ModelMessage - the raw messages sent to the AI
 * - UIMessage - a higher-level message type that Vercel AI SDK vends that
 *   groups together tool calls and reasoning traces with the message content.
 * - RowMessage - a database-friendly message type that decomposes parts of the messaage into table columns
 */
import * as z from "zod/v4";
import {
  type UIDataTypes,
  type UIMessage,
  type UIMessageChunk,
  type UIMessagePart,
} from "ai";
import { monotonicUlid } from "@std/ulid";
import { UIMessagePartSchema } from "../ai-sdk/ui-message.ts";
import {
  DbUIMessagePartSchema,
  partsHaveValidTextPart,
} from "../ai-sdk/utils.ts";
import { IsoDateTimeSchema } from "./iso.ts";
import { UlidSchema } from "./ulid.ts";
import { type AppUITools } from "../tools/index.ts";

export const ChatMetadataSchema = z.object({
  project_id: UlidSchema,
  conversation_id: UlidSchema,
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
  thinking_summaries: z.array(z.string()).default([]),
});

export type ChatMetadata = z.infer<typeof ChatMetadataSchema>;

/** Schema for user chat messages sent from client to API */
export const UserChatMessageSchema = z.object({
  id: z.string().default(() => monotonicUlid()),
  role: z.literal("user"),
  parts: z
    .array(UIMessagePartSchema)
    .nonempty()
    .refine((data) => partsHaveValidTextPart(data as ChatMessagePart[]), {
      message: "Message must have at least one text part with non-empty text",
    }),
  metadata: ChatMetadataSchema,
});

export type UserChatMessage = ChatMessage & {
  role: "user";
  metadata: ChatMetadata;
};

/** Parse and cast to UserChatMessage (bridges Zod inference to AI SDK generics) */
export const parseUserChatMessage = (data: unknown): UserChatMessage =>
  UserChatMessageSchema.parse(data) as unknown as UserChatMessage;

/** Our custom UIMessage type */
export type ChatMessage = UIMessage<ChatMetadata, UIDataTypes, AppUITools>;
export type ChatMessagePart = UIMessagePart<UIDataTypes, AppUITools>;
export type ChatMessageChunk = UIMessageChunk<ChatMetadata, UIDataTypes>;

export const RowMessageSchema = z.object({
  id: UlidSchema,
  role: z.enum(["user", "assistant", "system"]),
  parts: z.unknown().describe("An array of message parts (JSON)"),
  metadata: ChatMetadataSchema,
  project_id: UlidSchema,
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
});

export type RowMessage = z.infer<typeof RowMessageSchema>;

export const rowMessageToUiMessage = (message: RowMessage): ChatMessage => {
  return {
    id: message.id,
    role: message.role,
    // deno-lint-ignore no-explicit-any
    parts: (message.parts as any[]).map((part) =>
      DbUIMessagePartSchema.parse(part),
    ),
    metadata: message.metadata,
  };
};
